import React, { useState, useCallback } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { format } from "date-fns";
import { Button, Card, Col, Form, FormControl, FormGroup, FormLabel, Row } from "react-bootstrap";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SectionHeading } from "../../components/section-heading";
import ContentWrapper from "../../components/content-wrapper";
import { getAriSnapshot } from "./infrastructure/ariServiceApi";
import { ErrorMessage } from "./components/ErrorMessage";
import { SpinnerLoader } from "./components/SpinnerLoader";

interface ErrorMessageProps {
  id: string;
  defaultMessage: string;
  serviceErrorDetail?: string;
}

const messages = defineMessages({
  entityName: {
    id: "ariSnapshot.entityName",
    defaultMessage: "ARI Snapshot",
  },
  export: {
    id: "default.export",
    defaultMessage: "Export results",
  },
  getAriSnapshotError: {
    id: "ariSnapshot.getAriSnapshotError",
    defaultMessage: "Error on getting ari snapshot",
  },
  queryInstantGreaterThanNow: {
    id: "ariSnapshot.form.helpers.queryInstantLessThanNow",
    defaultMessage: "The snapshot's datetime must be less than now",
  },
  invalidQueryInstant: {
    id: "ariSnapshot.form.validations.invalidQueryInstant",
    defaultMessage: "Please enter a valid datetime",
  },
  invalidPropertyTicker: {
    id: "ariSnapshot.form.validations.invalidPropertyTicker",
    defaultMessage: "Please enter a valid ticker name",
  },
  invalidAriRangeStartDate: {
    id: "ariSnapshot.form.validations.invalidAriRangeStartDate",
    defaultMessage: "Please enter a valid ARI start date",
  },
  invalidAriRangeEndDate: {
    id: "ariSnapshot.form.validations.invalidAriRangeEndDate",
    defaultMessage: "Please enter a valid ARI end date",
  },
});

function AriSnapshot() {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorMessageProps | null>(null);
  const [propertyTickerSelected, setPropertyTickerSelected] = useState("");
  const [queryInstantSelected, setQueryInstantSelected] = useState("");
  const [ariDaysStartDateSelected, setAriDaysStartDateSelected] = useState("");
  const [ariDaysEndDateSelected, setAriDaysEndDateSelected] = useState("");
  const [propertyTickerIsInvalid, setPropertyTickerIsInvalid] = useState(false);
  const [queryInstantIsInvalid, setQueryInstantIsInvalid] = useState(false);
  const [ariDaysStartDateIsInvalid, setAriDaysStartDateIsInvalid] = useState(false);
  const [ariDaysEndDateIsInvalid, setAriDaysEndDateIsInvalid] = useState(false);

  const handlePropertyTickerSelection = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    setPropertyTickerIsInvalid(value.length <= 0);
    setPropertyTickerSelected(value);
  }, []);

  const handleQueryInstantSelection = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    setQueryInstantIsInvalid(value.length <= 0);
    setQueryInstantSelected(format(new Date(value), "yyyy-MM-dd HH:mm"));
  }, []);

  const handleAriRangeStartDateSelection = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.currentTarget.value;
      setAriDaysStartDateIsInvalid(value.length <= 0);
      setAriDaysStartDateSelected(value);
    },
    []
  );

  const handleAriRangeEndDateSelection = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.currentTarget.value;
    setAriDaysEndDateIsInvalid(value.length <= 0);
    setAriDaysEndDateSelected(value);
  }, []);

  const handleSearch = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await getAriSnapshot({
        hotelTicker: propertyTickerSelected,
        queryInstant: queryInstantSelected,
        ariDaysRange: {
          startDate: ariDaysStartDateSelected,
          endDate: ariDaysEndDateSelected,
        },
        asDownloadable: true,
      });
      window.location.href = response.url;
    } catch (e) {
      //@ts-ignore
      console.error(e.message);
      //@ts-ignore
      setError({ ...messages.getAriSnapshotError, serviceErrorDetail: e.message });
    } finally {
      setLoading(false);
    }
  }, [
    propertyTickerSelected,
    queryInstantSelected,
    ariDaysStartDateSelected,
    ariDaysEndDateSelected,
  ]);

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...messages.entityName} />
      </SectionHeading>
      <ErrorMessage error={error} />
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Card.Body>
          <Row>
            <FormGroup as={Col} controlId="formGridPropertyTicker">
              <FormLabel>Property Ticker</FormLabel>
              <FormControl
                type="text"
                placeholder="Enter property ticker"
                onChange={handlePropertyTickerSelection}
                required={true}
                isInvalid={propertyTickerIsInvalid}
              />
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage(messages.invalidPropertyTicker)}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup as={Col} controlId="formGridPropertyTickerSnapshotDateTime">
              <FormLabel>Snapshot's Datetime</FormLabel>
              <FormControl
                type="datetime-local"
                placeholder="Enter the snapshot's datetime"
                onChange={handleQueryInstantSelection}
                required={true}
                isInvalid={queryInstantIsInvalid}
              />
              <Form.Text className="text-muted">
                {intl.formatMessage(messages.queryInstantGreaterThanNow)}
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage(messages.invalidQueryInstant)}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup as={Col} controlId="formGridAriStartDate">
              <FormLabel>Start Date</FormLabel>
              <FormControl
                type="date"
                placeholder="Enter the ari start date range"
                onChange={handleAriRangeStartDateSelection}
                required={true}
                isInvalid={ariDaysStartDateIsInvalid}
              />
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage(messages.invalidAriRangeStartDate)}
              </Form.Control.Feedback>
            </FormGroup>

            <FormGroup as={Col} controlId="formGridAriEndDate">
              <FormLabel>End Date</FormLabel>
              <FormControl
                type="date"
                placeholder="Enter the ari end date range"
                onChange={handleAriRangeEndDateSelection}
                required={true}
                isInvalid={ariDaysEndDateIsInvalid}
              />
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage(messages.invalidAriRangeEndDate)}
              </Form.Control.Feedback>
            </FormGroup>
          </Row>
          <Button disabled={loading} variant="primary" className="mt-3 mr-3" onClick={handleSearch}>
            <SpinnerLoader loading={loading} />
            {!loading && intl.formatMessage(messages.export)}
          </Button>
        </Card.Body>
      </Card>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(AriSnapshot);
